import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import UserAvatar from './UserAvatar';
import { Loader2, Save, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Owners always receive the digest; managers are opt-in per store and get a store-scoped copy.
export default function DigestRecipients() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/kpi/digest/recipients`)
      .then(res => {
        setData(res.data);
        setSelected(new Set(res.data.managers.filter(m => m.selected).map(m => m.id)));
      })
      .catch(() => toast.error('Could not load recipients'));
  }, []);

  const byStore = useMemo(() => {
    const groups = new Map();
    (data?.managers || []).forEach(m => {
      const key = m.store_name || 'All stores';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    });
    return [...groups.entries()].sort(([a], [b]) => (a === 'All stores' ? -1 : b === 'All stores' ? 1 : a.localeCompare(b)));
  }, [data]);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/kpi/digest/recipients`, { manager_ids: [...selected] });
      toast.success(`Digest recipients saved — ${selected.size} manager${selected.size === 1 ? '' : 's'} added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save recipients');
    } finally { setSaving(false); }
  };

  if (!data) return <div className="py-6 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading recipients…</div>;

  return (
    <div className="space-y-4" data-testid="digest-recipients">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Always receives (owners)</p>
        <div className="flex flex-wrap gap-2">
          {data.owners.map(o => (
            <span key={o.id} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 rounded-full pl-1 pr-2.5 py-1">
              <UserAvatar name={o.name} picture={o.picture} size="xs" />{o.name}
            </span>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Also send to managers (each gets their own store's numbers)</p>
        {byStore.length === 0 && <p className="text-sm text-slate-400">No approved managers yet.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          {byStore.map(([store, members]) => (
            <div key={store} className="border border-slate-200 rounded-lg p-3 bg-white">
              <p className="text-xs font-bold text-slate-700 mb-2">{store}</p>
              <div className="space-y-2">
                {members.map(m => (
                  <label key={m.id} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`digest-recipient-${m.id}`}>
                    <Checkbox checked={selected.has(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`digest-recipient-check-${m.id}`} />
                    <UserAvatar name={m.name} picture={m.picture} size="xs" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate text-slate-800">{m.name}</span>
                      <span className="block truncate text-[11px] text-slate-400">{m.email} · {m.role.replace('_', ' ')}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-400">{data.updated_at ? `Last saved ${new Date(data.updated_at).toLocaleString()}${data.updated_by ? ` by ${data.updated_by}` : ''}` : 'Not customised yet — only owners receive it.'}</p>
        <Button size="sm" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="digest-recipients-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save recipients
        </Button>
      </div>
    </div>
  );
}
